/**
 * JuicePack — Visual effects system
 *
 * Everything that makes the game FEEL good:
 * - Screen shake (intensity + decay)
 * - Hit-stop / freeze frames
 * - Particle system (object-pooled)
 * - Bloom (ctx.globalCompositeOperation + shadowBlur)
 * - Trails (alpha fill instead of clearRect — handled in Engine)
 * - Flash effects
 */

export class JuicePack {
  constructor() {
    // Screen shake
    this._shakeIntensity = 0;
    this._shakeDuration = 0;
    this._shakeTimer = 0;
    this._shakeX = 0;
    this._shakeY = 0;

    // Hit-stop (freeze frames)
    this._freezeFrames = 0;

    // Screen flash
    this._flashColor = null;
    this._flashDuration = 0;
    this._flashTimer = 0;

    // Particle pool
    this._particles = [];
    this._particlePool = [];
    this._maxParticles = 500;
  }

  // --- Screen Shake ---

  shake(intensity = 8, duration = 0.2) {
    // Don't reduce existing shake
    if (intensity > this._shakeIntensity) {
      this._shakeIntensity = intensity;
    }
    this._shakeDuration = Math.max(this._shakeDuration, duration);
    this._shakeTimer = this._shakeDuration;
  }

  applyShake(ctx) {
    if (this._shakeTimer > 0) {
      const progress = this._shakeTimer / this._shakeDuration;
      const intensity = this._shakeIntensity * progress;
      this._shakeX = (Math.random() - 0.5) * intensity * 2;
      this._shakeY = (Math.random() - 0.5) * intensity * 2;
      ctx.translate(this._shakeX, this._shakeY);
    }
  }

  // --- Hit-Stop ---

  freeze(frames = 4) {
    this._freezeFrames = Math.max(this._freezeFrames, frames);
  }

  isFrozen() {
    return this._freezeFrames > 0;
  }

  tickFreeze() {
    if (this._freezeFrames > 0) this._freezeFrames--;
  }

  // --- Screen Flash ---

  flash(color = 'rgba(255,255,255,0.3)', duration = 0.08) {
    this._flashColor = color;
    this._flashDuration = duration;
    this._flashTimer = duration;
  }

  // --- Particles ---

  emit(x, y, count, options = {}) {
    const {
      color = '#ffffff',
      speed = 150,
      speedVariance = 0.5,
      size = 3,
      sizeVariance = 0.5,
      life = 0.5,
      lifeVariance = 0.3,
      gravity = 0,
      friction = 0.98,
      glow = true,
      shape = 'circle', // 'circle' | 'square' | 'line'
    } = options;

    for (let i = 0; i < count; i++) {
      if (this._particles.length >= this._maxParticles) break;

      const particle = this._particlePool.length > 0
        ? this._particlePool.pop()
        : {};

      const angle = Math.random() * Math.PI * 2;
      const spd = speed * (1 + (Math.random() - 0.5) * speedVariance * 2);

      particle.x = x;
      particle.y = y;
      particle.vx = Math.cos(angle) * spd;
      particle.vy = Math.sin(angle) * spd;
      particle.size = size * (1 + (Math.random() - 0.5) * sizeVariance * 2);
      particle.life = life * (1 + (Math.random() - 0.5) * lifeVariance * 2);
      particle.maxLife = particle.life;
      particle.color = color;
      particle.gravity = gravity;
      particle.friction = friction;
      particle.glow = glow;
      particle.shape = shape;

      this._particles.push(particle);
    }
  }

  /** Directional burst (e.g., bullet impact) */
  burst(x, y, count, angle, spread, options = {}) {
    const {
      color = '#ffffff',
      speed = 200,
      speedVariance = 0.3,
      size = 2,
      life = 0.3,
      glow = true,
    } = options;

    const spreadRad = (spread * Math.PI) / 180;

    for (let i = 0; i < count; i++) {
      if (this._particles.length >= this._maxParticles) break;

      const particle = this._particlePool.length > 0
        ? this._particlePool.pop()
        : {};

      const a = angle + (Math.random() - 0.5) * spreadRad;
      const spd = speed * (1 + (Math.random() - 0.5) * speedVariance * 2);

      particle.x = x;
      particle.y = y;
      particle.vx = Math.cos(a) * spd;
      particle.vy = Math.sin(a) * spd;
      particle.size = size * (0.5 + Math.random());
      particle.life = life * (0.5 + Math.random());
      particle.maxLife = particle.life;
      particle.color = color;
      particle.gravity = 0;
      particle.friction = 0.95;
      particle.glow = glow;
      particle.shape = 'circle';

      this._particles.push(particle);
    }
  }

  // --- Update ---

  update(dt) {
    // Shake decay
    if (this._shakeTimer > 0) {
      this._shakeTimer -= dt;
      if (this._shakeTimer <= 0) {
        this._shakeIntensity = 0;
        this._shakeDuration = 0;
      }
    }

    // Flash decay
    if (this._flashTimer > 0) {
      this._flashTimer -= dt;
    }

    // Particle update
    for (let i = this._particles.length - 1; i >= 0; i--) {
      const p = this._particles[i];
      p.life -= dt;
      if (p.life <= 0) {
        this._particlePool.push(p);
        this._particles.splice(i, 1);
        continue;
      }
      p.vy += p.gravity * dt;
      p.vx *= p.friction;
      p.vy *= p.friction;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
    }
  }

  // --- Draw ---

  drawParticles(ctx) {
    for (const p of this._particles) {
      const alpha = p.life / p.maxLife;
      const size = p.size * alpha;

      ctx.globalAlpha = alpha;

      if (p.glow) {
        ctx.shadowColor = p.color;
        ctx.shadowBlur = size * 3;
      }

      ctx.fillStyle = p.color;

      if (p.shape === 'square') {
        ctx.fillRect(p.x - size / 2, p.y - size / 2, size, size);
      } else {
        ctx.beginPath();
        ctx.arc(p.x, p.y, Math.max(0.5, size), 0, Math.PI * 2);
        ctx.fill();
      }

      if (p.glow) {
        ctx.shadowBlur = 0;
      }
    }
    ctx.globalAlpha = 1;
  }

  drawFlash(ctx, w, h) {
    if (this._flashTimer > 0) {
      const alpha = this._flashTimer / this._flashDuration;
      ctx.save();
      ctx.globalAlpha = alpha * 0.4;
      ctx.fillStyle = this._flashColor || '#ffffff';
      ctx.fillRect(0, 0, w, h);
      ctx.restore();
    }
  }

  // --- Utility ---

  get particleCount() {
    return this._particles.length;
  }

  clearParticles() {
    while (this._particles.length > 0) {
      this._particlePool.push(this._particles.pop());
    }
  }
}
